// src/pages/Videos.jsx
import { useState, useEffect } from 'react';

const Videos = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    const cargarVideos = async () => {
      try {
        const res = await fetch('https://logia-production-725b.up.railway.app/api/videos');
        const data = await res.json();
        if (data.success) {
          setVideos(data.data);
        }
      } catch (error) {
        console.error("Error cargando videos:", error);
      }
    };
    cargarVideos();
    window.scrollTo(0, 0);
  }, []);

  const formatearFecha = (fecha) => {
    const fechaFormat = new Date(fecha);
    return fechaFormat.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const videosFiltrados = videos.filter(vid => 
    vid.titulo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-[#C5A059] selection:text-[#050505] font-sans pt-24 flex flex-col">
      
      {/* Hero Section */}
      <section className="relative w-full pt-32 pb-16 md:pt-40 md:pb-24 flex flex-col items-center justify-center overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1517732306149-e8f829eb588a?w=2000')] bg-cover bg-center bg-no-repeat opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/90 via-[#050505]/60 to-[#050505]"></div>

        <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
          <p className="text-[#C5A059] tracking-[0.4em] uppercase text-xs md:text-sm font-light mb-4 animate-fade-in">
            Multimedia
          </p>
          <h1 className="font-serif text-4xl md:text-6xl font-normal text-white tracking-tight animate-fade-in [animation-delay:0.3s] drop-shadow-lg mb-6">
            Registro Audiovisual
          </h1>
          <p className="text-gray-400 font-light text-sm md:text-base max-w-2xl mx-auto animate-fade-in [animation-delay:0.6s]">
            Explore nuestra galería de conferencias, tenidas blancas, ceremonias conmemorativas y comunicados institucionales en formato de video.
          </p>
        </div>
      </section>

      {/* Contenido Principal */}
      <section className="relative z-20 max-w-[90rem] mx-auto px-6 lg:px-12 py-16 md:py-24 flex-grow w-full">
        
        {/* Panel de Búsqueda */}
        <div className="bg-gradient-to-br from-[#0a0a0a] to-[#111111] border border-white/5 p-6 md:p-8 mb-12 shadow-2xl flex flex-col md:flex-row gap-6 items-center justify-between">
          <div className="w-full md:w-1/2 relative">
            <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input 
              type="text" 
              placeholder="Buscar video por título..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#050505] border border-white/10 text-white pl-12 pr-4 py-4 focus:outline-none focus:border-[#C5A059] transition-colors placeholder-gray-600 font-light"
            />
          </div>
          <div className="text-gray-500 text-xs tracking-widest uppercase">
            Mostrando {videosFiltrados.length} videos
          </div>
        </div>

        {/* Grilla de Videos */}
        {videosFiltrados.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {videosFiltrados.map((video) => (
              <a 
                key={video.id} 
                href={video.youtube_url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col bg-[#0a0a0a] border border-white/5 hover:border-[#C5A059]/40 transition-all duration-500 shadow-xl overflow-hidden cursor-pointer"
              >
                
                {/* Portada del Video */}
                <div className="w-full aspect-video bg-gray-900 relative overflow-hidden">
                  <img 
                    src={video.cover_url || "https://images.unsplash.com/photo-1517732306149-e8f829eb588a?w=600"} 
                    alt={video.titulo} 
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
                  />
                  
                  {/* Botón de Reproducción al Medio */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full border border-white/30 bg-[#050505]/40 backdrop-blur-sm flex items-center justify-center group-hover:bg-[#C5A059] group-hover:border-[#C5A059] transition-all duration-300 transform group-hover:scale-110 shadow-lg">
                      <svg className="w-6 h-6 text-white group-hover:text-[#050505] ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div>
                  </div>

                  {/* Etiqueta de YouTube */}
                  <div className="absolute top-4 right-4">
                     <span className="bg-red-600/90 backdrop-blur-md text-white text-[10px] tracking-[0.1em] uppercase px-2 py-1 font-bold shadow-md flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/></svg>
                        YouTube
                     </span>
                  </div>
                </div>

                {/* Detalles del video */}
                <div className="p-6 flex flex-col flex-grow">
                  <span className="text-gray-500 text-[10px] tracking-widest uppercase font-mono mb-3 block">
                    Publicado: {formatearFecha(video.fecha_creacion)}
                  </span>
                  <h3 className="font-serif text-lg text-white group-hover:text-[#C5A059] transition-colors leading-snug">
                    {video.titulo}
                  </h3>
                </div>
                
              </a>
            ))}
          </div>
        ) : (
          <div className="text-center py-24 bg-[#0a0a0a] border border-white/5 shadow-lg">
            <svg className="w-12 h-12 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-400 text-lg font-light">No se encontraron videos con ese título.</p>
          </div>
        )}

      </section>
    </div>
  );
};

export default Videos;